import axios from 'axios'
import jwtDefaultConfig from './jwtDefaultConfig'
import toast from 'react-hot-toast'
import { saveReturnUrl, clearAuthSession } from '@src/utility/authRedirect'

export default class JwtService {
  // ** jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // ** For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // ** For Refreshing Token
  subscribers = []

  constructor(jwtOverrideConfig) {
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // ** Request Interceptor
    axios.interceptors.request.use(
      (config) => {
        // ** Get token from localStorage
        const accessToken = this.getToken()

        // ** If token is present add it to request's Authorization Header
        if (accessToken) {
          // ** eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // ** Add request/response interceptor
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const { config, response } = error
        const originalRequest = config

        // Guard against network errors where response is undefined
        if (!response) {
          return Promise.reject(error)
        }

        // Do not attempt refresh if the failed request was login, refresh, or OAuth validation
        const isAuthEndpoint =
          originalRequest?.url?.includes('/auth/login') ||
          originalRequest?.url?.includes('/auth/refresh-token') ||
          originalRequest?.url?.includes('/validateGoogleCreadential')

        if (response.status === 401 && !isAuthEndpoint) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.refreshToken()
              .then((newToken) => {
                this.isAlreadyFetchingAccessToken = false
                if (newToken) {
                  this.onAccessTokenFetched(newToken)
                } else {
                  this.handleSessionExpired()
                }
              })
              .catch(() => {
                this.isAlreadyFetchingAccessToken = false
                this.handleSessionExpired()
              })
          }

          const retryOriginalRequest = new Promise((resolve, reject) => {
            this.addSubscriber({
              resolve: (accessToken) => {
                originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
                resolve(axios(originalRequest))
              },
              reject: (err) => {
                reject(err)
              }
            })
          })
          return retryOriginalRequest
        } else if (response.status === 403) {
          toast.error('Unauthorized access!')
        }
        return Promise.reject(error)
      }
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers.forEach((subscriber) => {
      if (typeof subscriber?.resolve === 'function') {
        subscriber.resolve(accessToken)
      } else if (typeof subscriber === 'function') {
        subscriber(accessToken)
      }
    })
    this.subscribers = []
  }

  onAccessTokenFailed(error) {
    this.subscribers.forEach((subscriber) => {
      if (typeof subscriber?.reject === 'function') {
        subscriber.reject(error)
      }
    })
    this.subscribers = []
  }

  addSubscriber(subscriber) {
    this.subscribers.push(subscriber)
  }

  handleSessionExpired() {
    this.onAccessTokenFailed(new Error('Session Expired'))
    saveReturnUrl()
    clearAuthSession()
    toast.error('Session Expired. Please login again.')
    setTimeout(() => {
      window.location.replace('/login')
    }, 500)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setUserName(value) {
    localStorage.setItem(this.jwtConfig.setUserName, value)
  }

  setToken(value) {
    if (value) {
      localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
    } else {
      localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    }
  }

  setRefreshToken(value) {
    if (value) {
      localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
    } else {
      localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    }
  }

  login(...args) {
    return axios.post(this.jwtConfig.loginEndpoint, ...args)
  }

  loginWithOtp(...args) {
    return axios.post(this.jwtConfig.loginWithOTP, ...args)
  }

  loginWithOtpConfirm(...args) {
    return axios.post(this.jwtConfig.loginWithOTPConfirm, ...args)
  }

  forgotPassword(...args) {
    return axios.post(this.jwtConfig.forgotPassword, ...args)
  }

  forgotPasswordConfirm(...args) {
    return axios.put(this.jwtConfig.forgotPasswordConfirm, ...args)
  }

  onBoard(...args) {
    return axios.post(this.jwtConfig.onBoard, ...args)
  }

  register(...args) {
    return axios.post(this.jwtConfig.registerEndpoint, ...args)
  }

  async refreshToken() {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken) {
      return null
    }

    try {
      const response = await fetch(
        `${this.jwtConfig.refreshEndpoint}?refreshToken=${encodeURIComponent(refreshToken)}`,
        {
          method: 'POST',
        }
      )

      if (!response.ok) {
        return null
      }

      const resData = await response.json()
      const token =
        typeof resData.data === 'string'
          ? resData.data
          : resData.data?.jwtToken || resData.data?.accessToken

      if (token) {
        this.setToken(token)

        // Support refresh token rotation if returned by backend
        if (resData.data?.refreshToken) {
          this.setRefreshToken(resData.data.refreshToken)
        }

        return token
      }

      return null
    } catch (err) {
      console.warn('Refresh token request failed:', err)
      return null
    }
  }
}
export const showSuccessNotification = (message) => {
  return toast.success(message)
}

export const showErrorNotification = (message) => {
  return toast.error(message)
}
